import { useLayoutEffect, useRef, useState } from 'react';
import { tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';

import type { CommentId, Thread as ThreadModel, Reply as ReplyModel } from '../domains/model';

import { Footer } from './Footer';

const ReplyContainer = styled.div`
  border-top: 1px solid ${tokens.color.ui_border_light};
`;

const CommentsContainer = styled.div<{ scrollable: boolean }>`
  ${tokens.elevation(1)};

  background-color: ${tokens.color.ui_background};
  border-radius: ${tokens.radius.m};
  color: ${tokens.color.text_default};
  overflow: ${({ scrollable }) => (scrollable ? 'auto' : 'visible')};
  margin-bottom: ${tokens.spacer.x1};
`;

const CommentsFooter = styled(Footer)<{ scrollable: boolean }>`
  ${(props) =>
    props.scrollable
      ? `position: relative;
  margin-bottom: ${tokens.spacer.x3};`
      : ''}
`;

interface ThreadProps {
  thread: ThreadModel;
  onClose?: () => void;
  renderComment: (comment: ThreadModel | ReplyModel, onClose?: () => void) => React.ReactNode;
  renderCreator: (threadId: CommentId) => React.ReactNode;
  scrollable?: boolean;
}

export type { ThreadProps };

const useScrollToLatestReply = (
  containerRef: React.RefObject<HTMLDivElement>,
  replies: ReplyModel[],
) => {
  const [repliesLength, setRepliesLength] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef || !containerRef.current) {
      return;
    }

    const node = containerRef.current;

    if (replies.length <= repliesLength) {
      setRepliesLength(replies.length);
      return;
    }

    node.scrollTop = node.scrollHeight;

    setRepliesLength(replies.length);
  }, [containerRef, replies.length, setRepliesLength, repliesLength]);
};

export const Thread = ({
  thread,
  onClose,
  renderComment,
  renderCreator,
  scrollable = true,
}: ThreadProps): JSX.Element => {
  const { replies } = thread;
  const containerRef = useRef<HTMLDivElement>(null);
  // Set the initial length to 0 to ensure that we scroll to the bottom the first time
  // we open the comment.
  useScrollToLatestReply(containerRef, replies);

  return (
    <>
      <CommentsContainer
        data-testid="comments-container"
        ref={containerRef}
        scrollable={scrollable}
      >
        {renderComment(thread, onClose)}
        {replies.map((reply) => (
          <ReplyContainer key={reply.id}>{renderComment(reply)}</ReplyContainer>
        ))}
      </CommentsContainer>
      <CommentsFooter scrollable={scrollable}>{renderCreator(thread.id)}</CommentsFooter>
    </>
  );
};
